/**
 * O vocabulário de situação de requisição do ERP — em um lugar só (at-110).
 *
 * ⚠️ Espelha `SITUACOES_CONHECIDAS` do backend (`dto/compras-dashboard-query.dto.ts`), como
 * `RecorteDeRequisicao` já espelha o enum de recortes. Quem manda é o backend; esta cópia existe
 * porque a tela precisa montar um menu, e o frontend não importa do outro repositório.
 *
 * 🔴 Existe porque a lista já estava em DUAS cópias — `ROTULO_SITUACAO` no modal de recorte e
 * `rotuloDaSituacao` em `BlocosCompras` — e o filtro de status seria a terceira. A lista literal
 * de camadas de sync já derrubou coisa três vezes neste módulo pelo mesmo motivo (a última em
 * 21/09, com `ENTREGAS` faltando no `atualizadoEm`); repetir o padrão com as situações era só
 * questão de tempo.
 *
 * ⚠️ **Situação fora desta lista aparece como veio, nunca some**: o ERP pode passar a emitir um
 * valor novo, e escondê-lo faria a soma da tela não fechar sem ninguém ver. É a mesma razão pela
 * qual o backend **não valida** o parâmetro contra esta lista.
 */

export interface SituacaoDeRequisicao {
  valor: string;
  rotulo: string;
}

/** As 7 situações medidas em produção (10/09/2026), na ordem em que o trabalho acontece. */
export const SITUACOES_DE_REQUISICAO: SituacaoDeRequisicao[] = [
  { valor: 'pendente', rotulo: 'Pendente' },
  { valor: 'em_cotacao', rotulo: 'Em cotação' },
  { valor: 'em_negociacao', rotulo: 'Em negociação' },
  { valor: 'pedido_gerado', rotulo: 'Pedido gerado' },
  { valor: 'parcialmente_atendida', rotulo: 'Parcialmente atendida' },
  { valor: 'atendida', rotulo: 'Atendida' },
  { valor: 'cancelada', rotulo: 'Cancelada' },
];

const POR_VALOR = new Map(SITUACOES_DE_REQUISICAO.map((s) => [s.valor, s.rotulo]));

/**
 * A legenda de uma seleção de status, para o modal de detalhamento (at-112).
 *
 * ⚠️ Mora aqui, com o resto do vocabulário, pelo mesmo motivo que fez este arquivo nascer: a
 * frase já existiria em dois lugares (o card de compradores e o de placas), e a terceira cópia
 * viria com o próximo card que ganhasse filtro.
 */
export function rotuloDosStatus(situacoes: string[]): string | undefined {
  if (situacoes.length === 0) return undefined;
  if (situacoes.length === 1) return `status "${rotuloDaSituacao(situacoes[0])}"`;
  return `${situacoes.length} status`;
}

/**
 * As situações do PEDIDO de compra que Compras usa (at-128) — o `situacao_gerencial` do Nasajon,
 * liberado pela Nasajon em 21/10/2026 a pedido nosso. Substituiu o `a_faturar` do ERP, que a área
 * não usa (at-122) e que se divide em aguardando documento, parcialmente liquidado e liquidado.
 */
export const SITUACOES_DE_PEDIDO: SituacaoDeRequisicao[] = [
  { valor: 'aberto', rotulo: 'Aberto' },
  { valor: 'aguardando_documento', rotulo: 'Aguardando documento' },
  { valor: 'parcialmente_liquidado', rotulo: 'Parcialmente liquidado' },
  { valor: 'liquidado', rotulo: 'Liquidado' },
  { valor: 'cancelado', rotulo: 'Cancelado' },
];

/**
 * As opções do filtro de chegada do "Acompanhamento de entrega" (at-129) — os rótulos que a
 * coluna "Chegada" mostra (`SeloDaChegada`), e nenhum além deles.
 *
 * 🔴 "Aguardando" na tela são DOIS estados no servidor: a célula desenha `AGUARDANDO` e
 * `SEM_PREVISAO` com o mesmo selo. Uma opção por estado mostraria ao usuário um filtro que a
 * coluna não distingue; por isso a opção é uma só e `estadosDaChegada` a expande no envio.
 */
export const OPCOES_DE_CHEGADA: SituacaoDeRequisicao[] = [
  { valor: 'AGUARDANDO', rotulo: 'Aguardando' },
  { valor: 'PREVISAO_VENCIDA', rotulo: 'Previsão vencida' },
  { valor: 'CHEGOU_NO_PRAZO', rotulo: 'No prazo' },
  { valor: 'CHEGOU_ATRASADO', rotulo: 'Atrasado' },
  { valor: 'CHEGOU_SEM_PREVISAO', rotulo: 'Chegou sem previsão' },
];

/** O que cada opção do filtro de chegada significa no servidor. */
export function estadosDaChegada(opcoes: string[]): string[] {
  return opcoes.flatMap((o) => (o === 'AGUARDANDO' ? ['AGUARDANDO', 'SEM_PREVISAO'] : [o]));
}

/** A legenda de uma seleção de status de PEDIDO, para o modal de detalhamento (at-121). */
export function rotuloDosStatusDePedido(situacoes: string[]): string | undefined {
  if (situacoes.length === 0) return undefined;
  if (situacoes.length === 1) {
    const rotulo = SITUACOES_DE_PEDIDO.find((s) => s.valor === situacoes[0])?.rotulo ?? situacoes[0];
    return `status "${rotulo}"`;
  }
  return `${situacoes.length} status`;
}

/**
 * O rótulo da situação gerencial de um PEDIDO (at-129) — a coluna "Situação" da lista.
 *
 * ⚠️ `nao_sincronizada` é o pedido que a varredura completa ainda não regravou com o campo
 * (at-128): é ausência de dado, não um estado do pedido, e a tela diz isso por extenso.
 */
export function rotuloDaSituacaoDePedido(situacao: string | null | undefined): string {
  if (!situacao || situacao === 'nao_sincronizada') return 'Situação não sincronizada';
  return SITUACOES_DE_PEDIDO.find((s) => s.valor === situacao)?.rotulo ?? situacao;
}

/** O rótulo em português. Valor desconhecido volta como veio — nunca vira traço nem some. */
export function rotuloDaSituacao(situacao: string | null | undefined): string {
  if (!situacao) return '-';
  return POR_VALOR.get(situacao) ?? situacao;
}
